/*
 * File: ApptForm.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Creates the form used for creating/editing an appointment
 */
import React from "react";
import FormBuilder from "components/form-builder";
import FormModel from "../FormModel";
import UIProgress from "components/ui/UIProgress";
import PatientSearchDialog from "./PatientSearchDialog";
import ApptFormOptions from "../store/ApptFormOptions";
import Users from "services/users/Users";
import DataForFormSelect from "utils/DataForFormSelect";
import TransparenciesPreventionDialog from "./TransparenciesPreventionDialog";
import OverlappedAppointmentsDialog from "./OverlappedAppointmentsDialog";
import FormStore from "components/form-builder/FormStore";
import PropTypes from "prop-types";
import { StoreContext } from "utils/useStore";
import Preferences from "services/preferences/Store";
import ApptStore from "../store/DataStore";

class ApptForm extends React.Component {
  state = { saving: false, loading: true, showSearchDialog: false };
  static contextType = StoreContext;

  constructor(props) {
    super(props);
    this.resourceOptions = [{ id: "0", name: "None" }];
    this.typeOptions = [{ id: "0", name: "None" }];
    this.statusOptions = [{ id: "0", name: "None" }];
    this.providerOptions = [{ id: "0", name: "None" }];
    this.formStore = FormStore.create();
    this.resources = [];
  }

  get apptStore() {
    return this.context.getStore(ApptStore);
  }

  get apptFormOptions() {
    return this.context.getStore(ApptFormOptions);
  }

  async componentDidMount() {
    await this.setOptions();
    this.formStore.setFields(FormModel(this));
    this.setState({
      loading: false
    });

    // In the event that a user doesn't change the date or time, the other aliases for the appointmentDate on the appointment object don't get set correctly, since they are set in the onChange handler.
    this.props.appt.updateDateKeys(this.props.appt.appointmentDate);
  }

  toggleSearchDialog = () => {
    this.setState({
      showSearchDialog: !this.state.showSearchDialog
    });
  };

  patientSelectionResult = patient => {
    let appt = this.props.appt;
    appt.setPatient(patient);
    this.toggleSearchDialog();
  };

  onChange = (key, val) => {
    let appt = this.props.appt;
    appt.update(key, val);
    //When updating date/time, need to update appointmentTime, startTime, and start as well.
    if (key === "appointmentDate") {
      appt.updateDateKeys(val);
    }
    //Detects if any changes to the resource select option in the form
    if (key === "resourceId") {
      this.handleProvider(val, appt);
    }
  };

  //Used to change to the correct appointment provider if the resource changes while in the form
  handleProvider = (val, appt) => {
    let newProvider = "";
    //If no resource is selected, the patient provider is displayed
    if (val === "0") {
      newProvider = appt.patientProviderId;
    } else {
      const newResource = this.resources.find(
        res => res.resourceId === Number(val)
      );
      //If the new resource has a provider, it displays that provider. Otherwise, it displays the patient provider
      if (!newResource.providerId && appt.patientProviderId) {
        newProvider = appt.patientProviderId;
      } else newProvider = newResource.providerId;
    }

    if (Number(newProvider) > 1) {
      appt.update("providerID", newProvider);
    }
  };

  onSubmit = async () => {
    this.setState({ saving: true });
    let newAppointment = await this.props.appt.save(this.apptStore);
    let newAppointmentId = 0;
    this.setState({ saving: false });
    if (newAppointment && newAppointment.appointmentId)
      newAppointmentId = newAppointment.appointmentId;
    if (!this.props.appt.error) this.props.parentCallBack(newAppointmentId);
  };

  closeForm = newApptId => {
    this.props.parentCallBack(newApptId);
  };

  async setOptions() {
    const { appt } = this.props;
    if (appt.appointmentId === 0) {
      await appt.setNewApptDefaults(this.context.getStore(Preferences));
    }
    this.resources = await this.apptFormOptions.getResources();
    this.resourceOptions = this.resourceOptions.concat(
      DataForFormSelect(this.resources, "resourceId", "name")
    );
    this.statusOptions = this.statusOptions.concat(
      await this.apptFormOptions.getStatuses()
    );
    let types = await this.apptFormOptions.getTypes();
    this.typeOptions = this.typeOptions.concat(types);
    this.providerOptions = this.providerOptions.concat(
      await this.context.getStore(Users).forSelect("providers")
    );
  }

  render() {
    let { saving, loading, showSearchDialog } = this.state;
    return (
      <div>
        {(saving || loading) && <UIProgress />}
        {!saving && !loading && (
          <React.Fragment>
            <TransparenciesPreventionDialog
              appt={this.props.appt}
              parentCallBack={this.closeForm}
            />
            <OverlappedAppointmentsDialog appt={this.props.appt} />
            <PatientSearchDialog
              showSearchDialog={showSearchDialog}
              toggleSearchDialog={this.toggleSearchDialog}
              parentCallBack={this.patientSelectionResult}
            />
            <FormBuilder
              formStore={this.formStore}
              data={this.props.appt}
              parentOnChange={this.onChange}
              parentOnSubmit={this.onSubmit}
            />
          </React.Fragment>
        )}
      </div>
    );
  }
}

ApptForm.propTypes = {
  appt: PropTypes.object.isRequired,
  parentCallBack: PropTypes.func.isRequired
};

export default ApptForm;
